import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, GitBranchIcon, RadioIcon, NetworkIcon, GaugeIcon, RouteIcon, CheckCircle2Icon, AlertTriangleIcon } from 'lucide-react';
import { getCapacityLabel, getCapacityTier, getTierDistribution } from '../utils/linkCapacity';
import { getAlproLabel, isMetro } from '../utils/alpro';
import { UpstreamTree } from './UpstreamTree';
interface LinkRouteDrawerProps {
  site: any;
  isOpen: boolean;
  onClose: () => void;
  /** Menampilkan trace NE pilihan menuju Metro pada peta. */
  onTraceToMetro?: (siteId: string) => void;
  /** Membuka detail node lain tanpa menutup drawer. */
  onOpenSite?: (siteId: string) => void;
}
export function LinkRouteDrawer({
  site,
  isOpen,
  onClose,
  onTraceToMetro,
  onOpenSite
}: LinkRouteDrawerProps) {
  if (!site) return null;
  const targetSite = site.site_id || site.site_id_ne;

  // Link keluar: node ini berperan sebagai NE.
  const children = site.children || [];
  // Link masuk: node ini berperan sebagai FE, dengan NE dari site lain.
  const incomingLinks = site.parents || [];

  // 🔥 summary
  const radioCount = children.filter((c: any) => c.alpro_type?.toLowerCase().includes('radio')).length;
  const metroCount = children.filter((c: any) => isMetro(c.alpro_type)).length;

  // 🔥 distribusi Grouping_BW
  const bwDistribution = getTierDistribution(children);
  const maxGroupCount = bwDistribution.length ? Math.max(...bwDistribution.map(d => d.count)) : 0;

  // 🔥 jalur hasil trace ke Metro (dikirim dari LinkRoute.tsx)
  const path = site.path as {
    startId: string;
    hops: any[];
    reachedMetro: boolean;
    metroNode: string | null;
  } | undefined;
  const siteTier = getCapacityTier(site);
  return <AnimatePresence data-id="element-250" data-mp="3d33j01gp9hv9">
      {isOpen && <>
          {/* OVERLAY */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40" data-id="element-251" data-mp="3i2f0xdssytef" />

          {/* DRAWER */}
          <motion.div initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 25
      }} className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-slate-900 border-l border-slate-800 z-50 flex flex-col" data-id="element-252" data-mp="2u59zpc7j5gr4">
            {/* HEADER */}
            <div className="p-5 border-b border-slate-800 flex justify-between items-center" data-id="element-253" data-mp="3cxb4swawe38z">
              <div data-id="element-254" data-mp="37y8bg94a5lgu">
                <h2 className="text-lg font-bold text-cyan-400" data-id="element-255" data-mp="37xqa4y2rgh22">
                  {targetSite}
                </h2>
                <p className="text-xs text-slate-400" data-id="element-256" data-mp="32z55mmgh7b7t">Node Detail</p>
              </div>

              <button onClick={onClose} className="p-2 hover:bg-slate-800 rounded" data-id="element-257" data-mp="1wkdyw24o8w5v">
                <XIcon size={18} data-id="element-258" data-mp="1rmm675w7d60m" />
              </button>
            </div>

            {/* CONTENT */}
            <div className="flex-1 overflow-y-auto p-5 space-y-6" data-id="element-259" data-mp="70mh6ko5zvqr">
              {/* 🔥 LINK INFO */}
              <section data-id="element-260" data-mp="bz7h0izi8deo">
                <div className="flex items-center gap-2 text-cyan-400 mb-3" data-id="element-261" data-mp="h050r005xfia">
                  <NetworkIcon size={16} data-id="element-262" data-mp="lrd9dtto4wue" />
                  <span className="text-xs uppercase" data-id="element-263" data-mp="lsew4vk1r7ry">Link Info</span>
                </div>

                <div className="bg-slate-950/50 p-4 rounded-lg space-y-3 text-sm" data-id="element-264" data-mp="qr83s7cnk4k7">
                  {site.site_id_ne && <div data-id="element-265" data-mp="uqgut4gb5oc2">
                      <span className="text-slate-500 text-xs" data-id="element-266" data-mp="utwao9ht31ej">
                        NE (Source)
                      </span>
                      <div className="text-slate-200 font-medium" data-id="element-267" data-mp="14qr8sra62zsa">
                        {site.site_id_ne}
                      </div>
                    </div>}

                  {site.site_id_fe && <div data-id="element-268" data-mp="36inli482mfon">
                      <span className="text-slate-500 text-xs" data-id="element-269" data-mp="36hujfgk24ecu">
                        FE (Target)
                      </span>
                      <div className="text-slate-200 font-medium" data-id="element-270" data-mp="vu7hntz76czi">
                        {site.site_id_fe}
                      </div>
                    </div>}

                  <div data-id="element-271" data-mp="19rw06v5sl81e">
                    <span className="text-slate-500 text-xs" data-id="element-272" data-mp="15sodb492zfrw">
                      Node Dianalisa
                    </span>
                    <div className="text-cyan-400 font-bold" data-id="element-273" data-mp="i3vpjv2m4e8d">{targetSite}</div>
                  </div>

                  <div data-id="element-274" data-mp="d4she1rc5rw6">
                    <span className="text-slate-500 text-xs" data-id="element-275" data-mp="d5kfpkcbi3q5">Type</span>
                    <div className="text-slate-200 font-medium" data-id="element-276" data-mp="qw5y77xv0bni">
                      {getAlproLabel(site.alpro_type)}
                    </div>
                  </div>

                  {/* 🔥 GROUPING BW */}
                  <div data-id="element-277" data-mp="3uiwit7zxy1l7">
                    <span className="text-slate-500 text-xs" data-id="element-278" data-mp="3ui4oe0i2y9wi">
                      Grouping BW
                    </span>
                    <div className="flex items-center gap-2 mt-1" data-id="element-279" data-mp="3gep9221n1o8n">
                      <span className="h-[3px] w-6 rounded-full flex-shrink-0" style={{
                    background: siteTier.color,
                    boxShadow: `0 0 6px ${siteTier.color}`
                  }} data-id="element-280" data-mp="3gde5a2870ghj" />
                      <span className="font-mono font-semibold text-sm" style={{
                    color: siteTier.color
                  }} data-id="element-281" data-mp="3bdi1a2zhwp1z">
                        {getCapacityLabel(site)}
                      </span>
                      <span className="text-[10px] text-slate-500 uppercase tracking-wider ml-auto" data-id="element-282" data-mp="3p5z98c760imb">
                        {siteTier.label}
                      </span>
                    </div>
                  </div>
                </div>
              </section>

              {/* 🔥 ROUTE TO METRO */}
              {path && path.hops.length > 0 && <section data-id="element-283" data-mp="2iokk6370syat">
                  <div className="flex items-center justify-between text-cyan-400 mb-3" data-id="element-284" data-mp="p8puam4w8jr4">
                    <div className="flex items-center gap-2" data-id="element-285" data-mp="k9dcwgwsk4vx">
                      <RouteIcon size={16} data-id="element-286" data-mp="kb7x1t08nsyy" />
                      <span className="text-xs uppercase" data-id="element-287" data-mp="fhv6vefwve43">Route to Metro</span>
                    </div>
                    <span className={`text-[10px] px-2 py-1 rounded flex items-center gap-1 ${path.reachedMetro ? 'bg-cyan-500/20 text-cyan-300' : 'bg-amber-500/20 text-amber-300'}`} data-id="element-288" data-mp="ak3i2takc844">
                      {path.reachedMetro ? <>
                          <CheckCircle2Icon size={11} data-id="element-289" data-mp="3nqn6as0slyj1" />
                          {path.hops.length} hop
                        </> : <>
                          <AlertTriangleIcon size={11} data-id="element-290" data-mp="1lxyz67l12qxz" />
                          Metro not found
                        </>}
                    </span>
                  </div>

                  <div className="bg-slate-950/50 p-4 rounded-lg" data-id="element-291" data-mp="1ygkr49ddhajt">
                    <ol className="relative border-l border-slate-700/70 ml-1.5 space-y-4" data-id="element-292" data-mp="1yeq5sty6vgf7">
                      {/* origin */}
                      <li className="ml-4 relative" data-id="element-293" data-mp="287fe5fyg0e6b">
                        <span className="absolute -left-[22px] top-1 w-3 h-3 rounded-full bg-cyan-400 border-2 border-slate-950 shadow-[0_0_8px_rgba(34,211,238,0.9)]" data-id="element-294" data-mp="2864ftvnrnyml" />
                        <div className="text-[10px] text-slate-500 uppercase tracking-wider" data-id="element-295" data-mp="1fj9wx36ll7b8">
                          Origin (NE)
                        </div>
                        <div className="text-sm font-semibold text-cyan-300 font-mono" data-id="element-296" data-mp="1pg4sbd7kqrla">
                          {path.startId}
                        </div>
                      </li>

                      {path.hops.map((hop: any, i: number) => {
                  const tier = getCapacityTier(hop.row);
                  const last = i === path.hops.length - 1;
                  const metroHop = isMetro(hop.row.alpro_type);
                  return <li key={i} className="ml-4 relative" data-id="element-297" data-mp="3gyocwky6fype" data-mpi={i}>
                            <span className="absolute -left-[22px] top-1 w-3 h-3 border-2 border-slate-950" style={{
                      background: tier.color,
                      borderRadius: last && metroHop ? '2px' : '999px',
                      transform: last && metroHop ? 'rotate(45deg)' : 'none',
                      boxShadow: `0 0 8px ${tier.color}`
                    }} data-id="element-298" data-mp="3vqzz9im32gqd" />

                            <div className="flex items-center gap-2 mb-0.5" data-id="element-299" data-mp="2kas25wpf7xm3">
                              <span className="text-[10px] text-slate-500 uppercase tracking-wider" data-id="element-300" data-mp="2fc71xxa65dxn">
                                Hop {i + 1}
                              </span>
                              <span className="text-[9px] px-1.5 py-0.5 rounded" style={{
                        color: metroHop ? '#67e8f9' : '#fdba74',
                        background: metroHop ? 'rgba(34,211,238,0.15)' : 'rgba(249,115,22,0.15)'
                      }} data-id="element-301" data-mp="zsr4bee0zqjr">
                                {getAlproLabel(hop.row.alpro_type)}
                              </span>
                              <span className="text-[9px] font-mono ml-auto font-semibold" style={{
                        color: tier.color
                      }} data-id="element-302" data-mp="73acahubibo2">
                                {getCapacityLabel(hop.row)}
                              </span>
                            </div>

                            <div className="text-sm text-slate-200 font-mono truncate" data-id="element-303" data-mp="2zqkx3ikc6puw">
                              → {hop.row.site_id_fe}
                            </div>

                            {last && metroHop && <div className="text-[10px] text-cyan-400 mt-0.5 font-semibold uppercase tracking-wider" data-id="element-304" data-mp="2pr2fv9vt9i6j">
                                Metro endpoint
                              </div>}
                          </li>;
                })}
                    </ol>
                  </div>
                </section>}

              {/* 🔥 ANAKAN */}
              <section data-id="element-305" data-mp="3di83wcbwngrx">
                <div className="flex items-center justify-between text-cyan-400 mb-3" data-id="element-306" data-mp="3dhq10wuqm4b1">
                  <div className="flex items-center gap-2" data-id="element-307" data-mp="1x484r3kvvv5q">
                    <GitBranchIcon size={16} data-id="element-308" data-mp="1x6ucb37ryany" />
                    <span className="text-xs uppercase" data-id="element-309" data-mp="224ng5pllusl2">Anakan</span>
                  </div>

                  <span className="text-xs text-slate-400" data-id="element-310" data-mp="k568spebfar3">
                    {children.length} link
                  </span>
                </div>

                <div className="bg-slate-950/50 p-3 rounded-lg space-y-3" data-id="element-311" data-mp="f98uspqiyghh">
                  {children.length === 0 ? <div className="text-slate-500 text-xs" data-id="element-312" data-mp="p5bwxvldmf4f">
                      Tidak ada anakan
                    </div> : children.map((c: any, i: number) => {
                const tier = getCapacityTier(c);
                return <div key={i} className="flex justify-between items-center gap-3 p-3 bg-slate-900 rounded-lg border border-slate-800 hover:border-cyan-400 transition" data-id="element-313" data-mp="a7ji75nnlura" data-mpi={i}>
                          <div className="min-w-0" data-id="element-314" data-mp="3dvbr4bvri3on">
                            <div className="text-cyan-400 font-medium text-sm truncate" data-id="element-315" data-mp="39vt5tf013qla">
                              {c.site_id_ne}
                            </div>
                            <div className="text-xs text-slate-500 truncate" data-id="element-316" data-mp="1cskk4g5cpv2n">
                              → {c.site_id_fe}
                            </div>
                          </div>

                          {/* BADGES */}
                          <div className="flex flex-col items-end gap-1 flex-shrink-0" data-id="element-317" data-mp="1rk2zqcuatnlh">
                            <span className={`text-[10px] px-2 py-1 rounded ${isMetro(c.alpro_type) ? 'bg-cyan-500/20 text-cyan-300' : 'bg-orange-500/20 text-orange-400'}`} data-id="element-318" data-mp="tw17vhz93r1v">
                              {getAlproLabel(c.alpro_type)}
                            </span>

                            <span className="text-[10px] font-mono font-semibold px-2 py-0.5 rounded border" style={{
                      color: tier.color,
                      borderColor: `${tier.color}55`,
                      background: `${tier.color}14`
                    }} data-id="element-319" data-mp="2f8umgop4ddqv">
                              {getCapacityLabel(c)}
                            </span>
                          </div>
                        </div>;
              })}
                </div>
              </section>

              {/* 🔥 RANTAI HULU — telusuri sampai ujung radio */}
              <section data-id="element-320" data-mp="3c1c4bqzkk90p">
                <div className="flex items-center justify-between text-cyan-400 mb-3" data-id="element-321" data-mp="3c2n8hrwxfisc">
                  <div className="flex items-center gap-2" data-id="element-322" data-mp="373an7blpejrv">
                    <GitBranchIcon size={16} data-id="element-323" data-mp="374u3h27d0utn" />
                    <span className="text-xs uppercase" data-id="element-324" data-mp="1a963yx43ey1t">
                      Rantai Radio ke Metro
                    </span>
                  </div>
                  <span className="text-xs text-slate-400" data-id="element-325" data-mp="159bduvs56cht">sampai ujung</span>
                </div>

                <UpstreamTree rootSiteId={targetSite} nodes={site.upstream || []} loading={Boolean(site.upstreamLoading)} truncated={Boolean(site.upstreamTruncated)} onTraceToMetro={onTraceToMetro} onOpenSite={onOpenSite} data-id="element-326" data-mp="2vvmy9jj8y9a" />
              </section>

              {/* 🔥 ANAKAN LANGSUNG SEBAGAI FE */}
              <section data-id="element-327" data-mp="3k5leb71kvzqg">
                <div className="flex items-center justify-between text-cyan-400 mb-3" data-id="element-328" data-mp="3k6vblu0lx3w1">
                  <div className="flex items-center gap-2" data-id="element-329" data-mp="36f840y17s8xy">
                    <GitBranchIcon size={16} data-id="element-330" data-mp="36cly12tz21hu" />
                    <span className="text-xs uppercase" data-id="element-331" data-mp="3bel26exjd2fu">
                      Anakan langsung sebagai FE
                    </span>
                  </div>
                  <span className="text-xs text-slate-400" data-id="element-332" data-mp="31fcbbuxyw3xp">
                    {incomingLinks.length} link masuk
                  </span>
                </div>

                <div className="bg-slate-950/50 p-3 rounded-lg space-y-3" data-id="element-333" data-mp="2hu2bltogr72v">
                  {incomingLinks.length === 0 ? <div className="text-slate-500 text-xs" data-id="element-334" data-mp="2dubdt60irqou">
                      Tidak ada NE lain yang mengarah ke site ini.
                    </div> : incomingLinks.map((link: any, i: number) => {
                const tier = getCapacityTier(link);
                return <div key={`${link.site_id_ne}-${link.site_id_fe}-${i}`} className="p-3 bg-slate-900 rounded-lg border border-slate-800" data-id="element-335" data-mp="2wrci0om91mrq" data-mpi={i}>
                          <div className="flex justify-between gap-3" data-id="element-336" data-mp="35kfvgwww9zmd">
                            <div className="min-w-0" data-id="element-337" data-mp="3kfvulgcc0lpm">
                              <div className="text-[10px] text-slate-500 uppercase tracking-wider" data-id="element-338" data-mp="3keay3l6gisp4">
                                NE asal
                              </div>
                              <div className="text-cyan-400 font-medium text-sm font-mono truncate" data-id="element-339" data-mp="3mltv85m98zx9">
                                {link.site_id_ne}
                              </div>
                              <div className="text-xs text-slate-500 truncate" data-id="element-340" data-mp="c9a8wt1uurc">
                                → {link.site_id_fe}
                              </div>
                            </div>

                            <div className="flex flex-col items-end gap-1 flex-shrink-0" data-id="element-341" data-mp="a22etdury9m3">
                              <span className="text-[10px] px-2 py-1 rounded" style={{
                        color: tier.color,
                        border: `1px solid ${tier.color}55`,
                        background: `${tier.color}14`
                      }} data-id="element-342" data-mp="e3nvlaafj81k">
                                {getCapacityLabel(link)}
                              </span>
                              <span className="text-[10px] text-slate-500" data-id="element-343" data-mp="2ibsblvdhy3ec">
                                {getAlproLabel(link.alpro_type)}
                              </span>
                            </div>
                          </div>

                          <div className="flex gap-2 mt-3 pt-3 border-t border-slate-800/70" data-id="element-344" data-mp="28itdnmw410i2">
                            <button onClick={() => onTraceToMetro?.(link.site_id_ne)} className="flex-1 px-2 py-1.5 rounded-md bg-cyan-500/10 border border-cyan-400/30 text-cyan-300 text-[10px] font-semibold hover:bg-cyan-500/20 transition-colors" data-id="element-345" data-mp="23jq69vsnqi6x">
                              Jalur NE → Metro
                            </button>
                            <button onClick={() => onOpenSite?.(link.site_id_ne)} className="px-2 py-1.5 rounded-md bg-slate-800 border border-slate-700 text-slate-300 text-[10px] font-semibold hover:bg-slate-700 transition-colors" data-id="element-346" data-mp="1fw8lwlbq2mdz">
                              Detail NE
                            </button>
                          </div>
                        </div>;
              })}
                </div>
              </section>

              {/* 🔥 BW GROUP DISTRIBUTION */}
              {bwDistribution.length > 0 && <section data-id="element-347" data-mp="2jcc00zpy4g9o">
                  <div className="flex items-center gap-2 text-cyan-400 mb-3" data-id="element-348" data-mp="2je51m0lpcaat">
                    <GaugeIcon size={16} data-id="element-349" data-mp="2oa3rxtqlc2dv" />
                    <span className="text-xs uppercase" data-id="element-350" data-mp="2ak0e9r5s1h01">
                      Grouping BW Distribution
                    </span>
                  </div>

                  <div className="bg-slate-950/50 p-4 rounded-lg space-y-2.5" data-id="element-351" data-mp="2ej99l0ggtmxp">
                    {bwDistribution.map(({
                tier,
                count
              }) => <div key={tier.id} className="flex items-center gap-3" data-id="element-352" data-mp="371wnnvx1o1pa">
                        <span className="text-[10px] font-mono w-20 flex-shrink-0 text-right" style={{
                  color: tier.color
                }} data-id="element-353" data-mp="vmea9ju9zim2">
                          {tier.label}
                        </span>

                        <div className="flex-1 h-1.5 bg-slate-800/80 rounded-full overflow-hidden" data-id="element-354" data-mp="15bfmcyxp9fur">
                          <div className="h-full rounded-full transition-all" style={{
                    width: `${count / maxGroupCount * 100}%`,
                    background: tier.color,
                    boxShadow: `0 0 8px ${tier.color}`
                  }} data-id="element-355" data-mp="10dw7pxx3b12a" />
                        </div>

                        <span className="text-[11px] font-mono font-semibold text-slate-300 w-6 text-right flex-shrink-0" data-id="element-356" data-mp="1y0uyjs13pes9">
                          {count}
                        </span>
                      </div>)}
                  </div>
                </section>}

              {/* 🔥 SUMMARY */}
              <section data-id="element-357" data-mp="2mryik4w3e3ev">
                <div className="flex items-center gap-2 text-cyan-400 mb-3" data-id="element-358" data-mp="2mshpyro499en">
                  <RadioIcon size={16} data-id="element-359" data-mp="30rr2nhd45ldv" />
                  <span className="text-xs uppercase" data-id="element-360" data-mp="30oli8fffj7oh">Summary</span>
                </div>

                <div className="bg-slate-950/50 p-4 rounded-lg space-y-2 text-sm text-slate-300" data-id="element-361" data-mp="2vqa803l82v5q">
                  <div data-id="element-362" data-mp="3an14o53ihsrn">
                    Total Anak: <b data-id="element-363" data-mp="35q0y7t2r2o00">{children.length}</b>
                  </div>
                  <div data-id="element-364" data-mp="35nokjs40yzsr">
                    Metro: <b data-id="element-365" data-mp="3jgxohd97et3w">{metroCount}</b>
                  </div>
                  <div data-id="element-366" data-mp="3jja6fqeyp3gy">
                    Radio IP: <b data-id="element-367" data-mp="3ffue369xdpa2">{radioCount}</b>
                  </div>
                </div>
              </section>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}